/// <reference lib="dom" />

import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';
import { asyncTimeout } from '../async/asyncTimeout';
import { exceptionToString, makeError } from './exception';
import type { Logger } from '../types/logger';

/**
 * Stubbing out the Vue.set type, to allow for extracting logic out that is reliant on this functionality.
 */
export type VueSet = (v: VueSetValue) => void;
export type VueDelete = (v: VueDeleteValue) => void;

export type VueDeleteValue = Pick<VueSetValue, 'key' | 'object'>;
export type VueSetValue = {
  object: Record<string | number, any>;
  key: string | number;
  value: any;
};

/**
 * Useful for marking 1 or more properties as optional in a given type.
 */
export type PartialBy<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

/**
 * Useful for marking 1 or more properties as required in a given type.
 */
export type RequiredBy<T, K extends keyof T>  = T & Required<Pick<T, K>>;

/**
 * For when mapping a subset of K to T
 */
export type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

/**
 * This helper type "completes" an interface/type by making all optional properties required but allows undefined.
 * Useful for when you want a type that ensures no property is omitted by mistake.
 */
export type CompleteType<T> = {
  [P in keyof Required<T>]: Pick<T, P> extends Required<Pick<T, P>> ? T[P] : (T[P] | undefined);
};

export function parseOrNull<T>(logger: Logger, value: string | null | undefined): T | null {

  if (!value) {
    return null;
  }

  let result: T | null;
  try {
    result = JSON.parse(value);
  } catch (err) {
    logger.error(`Error while parsing body: ${exceptionToString(err)}, body='${value}'`);
    return null;
  }
  return result;
}

export function nonEmptyOrUndefined<T>(value: T) {
  return isEmpty(value) ? undefined : value;
}

export const isArrayOfString = (x: unknown): x is string[] =>
  Array.isArray(x) && x.every(element => typeof element === 'string');

export const isArrayOfNumber = (x: unknown): x is number[] =>
  Array.isArray(x) && x.every(element => typeof element === 'number');

const isArrayOfPositiveInteger = (x: unknown): x is number[] => {
  if (!Array.isArray(x)) {
    return false;
  }

  return x.every(element => Number.isInteger(element) && element >= 0);
};

export const is2dArrayOfPositiveInteger = (x: unknown): x is number[][] => {
  if (!Array.isArray(x)) {
    return false;
  }

  if (x.length === 0) {
    return false;
  }

  return x.every(element => isArrayOfPositiveInteger(element));
};

/**
 * Checks whether a given value string|number is member of an enum
 * @param e Type of the Enum to test
 */
export const isEnum = <T extends Record<string, unknown>> (e: T) : (token: unknown) => token is T[keyof T] => {
  const keys = Object.keys(e).filter((k) => {
    return !/^\d/.test(k);
  });
  const values: ReadonlySet<unknown> = new Set(keys.map((k) => {
    return (e as any)[k];
  }));
  return (token: unknown): token is T[keyof T] => {
    return values.has(token);
  };
};

/**
 * The purpose of this function is to insert breaking points in
 * long running functions so that the event loop can be processed
 * instead of being blocked for too long. By sleeping 0 ms, we are
 * effectively deferring the subsequent operations to the next
 * iteration of the event loop.
 */
export function defer(): Promise<void> {
  return asyncTimeout(0);
}


// Datadog log format. not being used at the moment.
export const ddLog = (logger: Logger, metricName: string, metricValue: any, metricType = 'gauge') => {
  logger.info(`MONITORING|${Math.floor(new Date() as any / 1000)}|${metricValue}|${metricType}|${metricName}|`);
};

/**
 * @deprecated This function is trivial. Just inline it.
 */
export function padLeft(str: string, pad: string, length: number) {
  return str.padStart(length, pad);
}


function hexa_to_decimal(digit: string) {
  const digits = '0123456789abcdef';
  return digits.indexOf(digit);
}

/**
 * Converts a #rrggbbaa hexa string to rgba(#,#,#,#), returns undefined on
 * unexpected data.
 *
 * @param hexa the hexa string to convert
 */
export const getRgbaFromHex = (hexa: string): string | undefined => {
  if (!hexa || hexa.length !== 9 || !hexa.startsWith('#')) {
    return undefined;
  }

  hexa = hexa.toLowerCase();
  const red = hexa_to_decimal(hexa.charAt(1)) * 16 + hexa_to_decimal(hexa.charAt(2));
  const green = hexa_to_decimal(hexa.charAt(3)) * 16 + hexa_to_decimal(hexa.charAt(4));
  const blue = hexa_to_decimal(hexa.charAt(5)) * 16 + hexa_to_decimal(hexa.charAt(6));

  // Alpha needs to be a value between 0 and 1 in the CSS world.
  const alpha = (hexa_to_decimal(hexa.charAt(7)) * 16 + hexa_to_decimal(hexa.charAt(8))) / 255;

  return `rgba(${red},${green},${blue},${alpha})`;
};

export const entries = Object.entries as <T>(
  o: T
) => [Extract<keyof T, string>, T[keyof T]][];

export const isValidGUID = (guid?: string): boolean => {
  if (!guid) {
    return false;
  }
  const GUIDValidatorRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return GUIDValidatorRegex.test(guid);
};

export const isProxyFile = (filename: string): boolean => {
  if (!filename) {
    return false;
  }
  const ValidatorRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-proxy.mp4$/;
  return ValidatorRegex.test(filename);
};

export const isTsProxyFile = (filename: string): boolean => {
  if (!filename) {
    return false;
  }
  const ValidatorRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-proxy-tsr[0-9]+,[0-9]+.ts$/;
  return ValidatorRegex.test(filename);
};

/**
 * Splits a string at a given index and returns the two parts (excluding the value at given index) in an array
 * @param index Index to split the string at
 */
export const splitAt = (index: number) => (x: string): Array<string> => index === -1 ? [x] : [x.slice(0, index), x.slice(index + 1)];

/**
 * Splits array into two at a given index and returns the two parts (including the value at given index) in an array
 * @param index Index to split the array at
 */
export const splitArray = <T>(x: Array<T>, index: number) => index === -1 ? [x] : [x.slice(0, index), x.slice(index)];

export const getNormalizedHexString = (s: string | undefined): string | undefined => {
  if (!isHexString(s)) {
    return s;
  }

  return s!.substr(2).toUpperCase();
};


export const isHexString = (s: string | undefined) => {
  if (typeof s !== 'string') {
    return false;
  }

  if (!s.startsWith('0x') && !s.startsWith('0X')) {
    return false;
  }

  return true;
};

export const isAlphaNumeric = (s: string | undefined) => {
  return typeof s === 'string' && /^[0-9A-Za-z]+$/i.test(s);
};

const escapeRegExp = (str: string) => {
  return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export const replaceAll = (str: string, find: string, replace: string) => {
  // we need to use the function syntax to get around issues with $$ becoming $ while replacing.
  return str.replace(new RegExp(escapeRegExp(find), 'g'), () => replace);
};

export const getPropertiesWithDifferentValues = <Properties extends Record<string, any>>(oldObject: Properties | undefined, newObject: Properties | undefined): (keyof Properties)[] => {
  const oldKeys = Object.keys(oldObject ?? {}) as Array<keyof Properties>;
  const newKeys = Object.keys(newObject ?? {}) as Array<keyof Properties>;
  const uniqueProperties = new Set<keyof Properties>([...oldKeys, ...newKeys]);
  const propertiesWithDifferentValues = [...uniqueProperties].filter(property =>
    (!isPropertyEmpty(oldObject?.[property]) || !isPropertyEmpty(newObject?.[property])) &&
    !isEqual(oldObject?.[property], newObject?.[property]));
  return propertiesWithDifferentValues;
};


export const getPropertiesWithDifferentValuesIncludingNull = <Properties extends Record<string, any>>(oldObject: Properties | undefined, newObject: Properties | undefined): (keyof Properties)[] => {
  const oldKeys = Object.keys(oldObject ?? {}) as Array<keyof Properties>;
  const newKeys = Object.keys(newObject ?? {}) as Array<keyof Properties>;
  const uniqueProperties = new Set<keyof Properties>([...oldKeys, ...newKeys]);
  const propertiesWithDifferentValues = [...uniqueProperties].filter(property =>
    ((!isPropertyEmpty(oldObject?.[property]) || oldObject?.[property] === null) || ((!isPropertyEmpty(newObject?.[property])) || newObject?.[property] === null)) &&
    !isEqual(oldObject?.[property], newObject?.[property]));
  return propertiesWithDifferentValues;
};

// sets the result of the promise as the prop on the obj
export const setPropFromPromise = async (obj: any, prop: any, promise: Promise<any>) => {
  obj[prop] = await promise;
};


/**
 * Awaits the given async function for maximum timeout milliseconds.
 * If it does not complete by then, it throws a MimirFunctionTimedOutError exception.
 */
export function awaitWithTimeout<T>(
  asyncFn: () => Promise<T>,
  asyncFnName: string,
  timeout: number,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let result: Promise<T>;
    try {
      result = asyncFn();
    } catch(err) {
      reject(err as Error);
      return;
    }

    const timeoutObject = setTimeout(() => {
      reject(makeError('Function timed out', 'MimirFunctionTimedOutError', { info: { asyncFnName, timeout } }));
    }, timeout);

    result.then(val => {
      clearTimeout(timeoutObject);
      resolve(val);
    }).catch(err => {
      clearTimeout(timeoutObject);
      reject(err as Error);
    });
  });
}

export function isValidHttpsUrl(url: string) {
  if (isEmpty(url)) {
    return false;
  }
  let urlObj = undefined;
  try {
    urlObj = new URL(url);
  } catch(err) {
    return false;
  }

  // For local testing, we want to allow http instead of https
  if (url.startsWith('http://127.0.0.1:') || url.startsWith('http://localhost:')) {
    return true;
  }
  return urlObj.protocol === 'https:';
}

export function getSanitizedUrlsList(commaSeparatedUrls: string) {
  const urls = commaSeparatedUrls.split(',').map(url => url.trim());
  return uniq(urls.filter(url => isValidHttpsUrl(url)));
}

/**
 * Unlike isEmpty() this function also checks boolean and number metadata fields.
 */
export function isPropertyEmpty(field: unknown): boolean {
  if (isBoolean(field) || isNumber(field)) {
    return false;
  }
  return isEmpty(field);
}

/**
 * Extracts the type inside Promise<> i.e. the awaited type.
 * Built-in type in typescript 4.5+
 */
export type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

export const initializeMapEntry = <T>(map: Record<string, T>, key: string, initialValue: T) => {
  if (!map[key]) {
    map[key] = initialValue;
  }
};

/**
 * Used for having the compiler verify unreachable code,
 * such after an if-else that should be exhaustive, and then
 * getting a error message with the unexpected value
 * if the assertion does not hold at runtime.
 */
export function assertUnreachable(x: never): never {
  throw new Error('assertUnreachable invoked with: ' + x);
}
